import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import type { components } from '@/shared/api/contracts.generated';
import { OptionDto, RemoteOptions } from '@/ui/forms/types';
import { environment } from '@env/environment';
import { LookupHandler } from '../lookup-handler';

interface CierreCajaUsuarioLookupScope {
  year: number;
  month: number;
  empresaId?: number;
  bancoCuentaId?: number;
}

type CierreCajaUsuarioOptionsResponse = components['schemas']['org.springframework.data.web.PagedModelCom.databi.erp_taxis.common.base_domains.OptionDTO'];

function isCierreCajaUsuarioLookupScope(value: unknown): value is CierreCajaUsuarioLookupScope {
  if (typeof value !== 'object' || value === null) return false;

  const scope = value as Partial<CierreCajaUsuarioLookupScope>;
  return Number.isInteger(scope.year) && Number.isInteger(scope.month);
}

@Injectable({ providedIn: 'root' })
export class CierreCajaUsuariosLookupHandler implements LookupHandler {
  private readonly http = inject(HttpClient);

  supports(table: string): boolean {
    return table === 'cierre_caja_usuario';
  }

  handle(remote: RemoteOptions, term: string): Observable<OptionDto[]> {
    if (!isCierreCajaUsuarioLookupScope(remote.filter)) return of([]);

    const scope = remote.filter;
    let params = new HttpParams()
      .set('year', String(scope.year))
      .set('month', String(scope.month))
      .set('q', term ?? '')
      .set('page', '0')
      .set('size', String(remote.pageSize ?? 20));

    if (scope.empresaId != null) params = params.set('empresaId', String(scope.empresaId));
    if (scope.bancoCuentaId != null) params = params.set('bancoCuentaId', String(scope.bancoCuentaId));

    return this.http.get<CierreCajaUsuarioOptionsResponse>(
      `${environment.api}/api/tesoreria/cierre-caja/usuarios/options`,
      { params }
    ).pipe(
      map(response => (response.content ?? [])
        .filter((option): option is { id: number; label: string } => option.id != null && !!option.label)
        .map(option => ({ id: option.id, label: option.label }))),
      catchError(() => of([]))
    );
  }
}
